import { ArrowDownLeft, ArrowUpRight, ChevronRight, Repeat } from "lucide-react";
import Flag from "react-world-flags";

import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { RecentTransaction } from "../types/wallets-api.types";
import { currencyFractionDigits, resolveLocale } from "../utils/formatWalletAmount";

interface WalletMovementRowProps {
  transaction: RecentTransaction;
  /** Country flag code (ISO2 / ISO3) for the wallet this row belongs to. Hidden when null/crypto. */
  flagCode?: string | null;
  onClick: (tx: RecentTransaction) => void;
}

const formatTimestamp = (ts: string, locale: string): string => {
  if (!ts) return "";
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return ts;
  return new Intl.DateTimeFormat(resolveLocale(locale), {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

/**
 * One posted ledger leg as a statement row. Legs are settled facts, so there is no status column — the
 * direction is carried by the sign/color of the amount (handoff §3.5: CREDIT `+` green, DEBIT `−`).
 */
export const WalletMovementRow = ({ transaction, flagCode, onClick }: WalletMovementRowProps) => {
  const { t, activeLanguage } = useTranslations("wallets");
  const inbound = transaction.amount > 0;
  const isInternal = transaction.kind === "internal";
  const decimals = currencyFractionDigits(transaction.currency);

  return (
    <button
      type="button"
      onClick={() => onClick(transaction)}
      className="grid w-full grid-cols-[auto_1fr_auto] items-center gap-3.5 border-b border-border bg-background px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-muted/40 focus:bg-muted/40 focus:outline-none sm:grid-cols-[auto_1fr_auto_auto]"
    >
      {/* Icon */}
      <div
        className={cn(
          "grid h-8 w-8 shrink-0 place-items-center rounded-md border",
          isInternal
            ? "border-primary/25 bg-primary/10 text-primary"
            : inbound
              ? "border-primary/20 bg-primary/10 text-primary"
              : "border-border bg-muted text-foreground/70",
        )}
        aria-hidden="true"
      >
        {isInternal ? (
          <Repeat size={14} />
        ) : inbound ? (
          <ArrowDownLeft size={14} />
        ) : (
          <ArrowUpRight size={14} />
        )}
      </div>

      {/* Title + subtitle */}
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-[13px] font-medium text-foreground">{transaction.title}</span>
          {/* The rail keeps its breakpoint: on a phone it is the one tag worth the width, since internal vs
              external is already carried by the icon and the sign. */}
          {transaction.method && (
            <ColocaBadge variant="secondary" className="shrink-0 px-1.5 py-0 text-[9.5px]">
              {transaction.method}
            </ColocaBadge>
          )}
          <ColocaBadge
            variant={isInternal ? "primary" : "default"}
            className="hidden shrink-0 px-1.5 py-0 text-[9.5px] sm:inline-flex"
          >
            {isInternal ? t("movements_detail_label_internal") : t("movements_detail_label_external")}
          </ColocaBadge>
        </div>
        {(transaction.subtitle || transaction.ts) && (
          <div className="mt-0.5 truncate text-[12px] text-muted-foreground">
            {transaction.subtitle}
            {transaction.subtitle && transaction.ts ? " · " : ""}
            {transaction.ts && (
              <span className="font-mono tracking-wide">
                {formatTimestamp(transaction.ts, activeLanguage)}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Currency + flag (desktop only) */}
      <div className="hidden items-center gap-1.5 text-muted-foreground sm:flex">
        {flagCode && (
          <Flag code={flagCode} className="h-2.5 w-3.5 rounded-[1.5px] object-cover" aria-hidden />
        )}
        <span className="font-mono text-[11px]">{transaction.currency}</span>
      </div>

      {/* Amount + chevron */}
      <div className="flex shrink-0 items-center gap-1.5">
        <ColocaAmountDisplay
          value={transaction.amount}
          minimumFractionDigits={decimals.min}
          maximumFractionDigits={decimals.max}
          locale={resolveLocale(activeLanguage)}
          signed
          size="sm"
          className={cn("block text-right text-[14px]", inbound ? "text-primary" : "text-foreground")}
        />
        <ChevronRight size={14} className="text-muted-foreground" aria-hidden />
      </div>
    </button>
  );
};
